import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  inject,
} from '@angular/core';

type CustomTooltipPlacement = 'above' | 'below';

@Component({
  selector: 'app-custom-tooltip',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './custom-tooltip.html',
  styleUrl: './custom-tooltip.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomTooltipComponent {
  @Input() text = '';
  @Input() tooltipText: string | null = null;
  @Input() maxLength = 25;
  @Input() showTooltipOnHover = false;

  @ViewChild('trigger', { static: true }) private readonly triggerRef?: ElementRef<HTMLElement>;
  @ViewChild('bubble') private readonly bubbleRef?: ElementRef<HTMLElement>;

  protected isOpen = false;
  protected tooltipPlacement: CustomTooltipPlacement = 'above';
  protected tooltipPosition = { top: 0, left: 0, arrowLeft: 0 };

  private readonly cdr = inject(ChangeDetectorRef);
  private positionFrame: number | null = null;
  private readonly tooltipGap = 12;
  private readonly viewportMargin = 8;
  private readonly arrowOffset = 16;

  protected get hasOverflow(): boolean {
    const value = typeof this.text === 'string' ? this.text.trim() : '';
    return value.length > this.maxLength;
  }

  protected get tooltipContent(): string {
    const value = typeof this.tooltipText === 'string' ? this.tooltipText.trim() : '';
    return value || this.text.trim();
  }

  protected get tooltipRows(): Array<{ label: string; value: string }> {
    const content = this.tooltipContent;

    if (!content.includes('\n')) {
      return [];
    }

    return content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const separatorIndex = line.indexOf(':');

        if (separatorIndex === -1) {
          return { label: line, value: '' };
        }

        return {
          label: line.slice(0, separatorIndex + 1).trim(),
          value: line.slice(separatorIndex + 1).trim(),
        };
      });
  }

  protected get shouldShowTooltip(): boolean {
    return this.showTooltipOnHover || this.hasOverflow;
  }

  protected get displayText(): string {
    const value = typeof this.text === 'string' ? this.text.trim() : '';
    if (!value) {
      return '-';
    }

    return value.length > this.maxLength ? `${value.slice(0, this.maxLength)}...` : value;
  }

  protected openTooltip(): void {
    if (this.shouldShowTooltip) {
      this.isOpen = true;
      this.queuePositionUpdate();
    }
  }

  protected closeTooltip(): void {
    this.isOpen = false;
    this.cancelPositionUpdate();
  }

  @HostListener('window:resize')
  protected onResize(): void {
    this.scheduleTooltipReposition();
  }

  @HostListener('window:scroll')
  protected onScroll(): void {
    this.scheduleTooltipReposition();
  }

  private scheduleTooltipReposition(): void {
    if (this.isOpen) {
      this.queuePositionUpdate();
    }
  }

  private queuePositionUpdate(): void {
    this.cancelPositionUpdate();
    this.positionFrame = window.requestAnimationFrame(() => {
      this.positionFrame = window.requestAnimationFrame(() => this.updateTooltipPosition());
    });
  }

  private cancelPositionUpdate(): void {
    if (this.positionFrame !== null) {
      window.cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }
  }

  private updateTooltipPosition(): void {
    const triggerElement = this.triggerRef?.nativeElement;
    const bubbleElement = this.bubbleRef?.nativeElement;

    if (!triggerElement || !bubbleElement) {
      return;
    }

    const triggerRect = triggerElement.getBoundingClientRect();
    const bubbleRect = bubbleElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const preferAbove = spaceAbove >= bubbleRect.height + this.tooltipGap + this.viewportMargin;
    const placement: CustomTooltipPlacement = preferAbove || spaceAbove >= spaceBelow ? 'above' : 'below';

    const top = placement === 'above'
      ? Math.max(this.viewportMargin, triggerRect.top - bubbleRect.height - this.tooltipGap)
      : Math.min(viewportHeight - bubbleRect.height - this.viewportMargin, triggerRect.bottom + this.tooltipGap);

    const centeredLeft = triggerRect.left + (triggerRect.width / 2) - (bubbleRect.width / 2);
    const left = Math.min(
      Math.max(this.viewportMargin, centeredLeft),
      Math.max(this.viewportMargin, viewportWidth - bubbleRect.width - this.viewportMargin),
    );

    const arrowLeft = Math.min(
      Math.max(this.arrowOffset, (triggerRect.left + (triggerRect.width / 2)) - left),
      bubbleRect.width - this.arrowOffset,
    );

    this.tooltipPlacement = placement;
    this.tooltipPosition = {
      top: Math.max(this.viewportMargin, top),
      left,
      arrowLeft,
    };
    this.cdr.markForCheck();
  }
}
